'use client';

import * as React from 'react';
import { useTenant } from '@/hooks/use-tenant';
import {
    useGetDocCommentsQuery,
    useCreateDocCommentMutation,
    useUpdateDocCommentMutation,
    useDeleteDocCommentMutation,
    useGetExternalDocCommentsQuery,
    useCreateExternalDocCommentMutation,
    useUpdateExternalDocCommentMutation,
    useDeleteExternalDocCommentMutation,
} from '@/state/project/project-api';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/rich-text';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { toast } from 'sonner';
import {
    Loader2,
    Send,
    MessageSquare,
    MoreVertical,
    Pencil,
    Trash2,
    Calendar,
    Save
} from 'lucide-react';
import { formatDate, isValid } from 'date-fns';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';

interface DocCommentsProps {
    docId: number;
    isExternal?: boolean;
}

const safeFormatDate = (dateStr: string | undefined | null, formatStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (!isValid(date)) return 'N/A';
    return formatDate(date, formatStr);
};

export const DocComments = ({ docId, isExternal = false }: DocCommentsProps) => {
    const { tenant } = useTenant();
    const [comment, setComment] = React.useState('');
    const [editingCommentId, setEditingCommentId] = React.useState<number | null>(null);
    const [editValue, setEditValue] = React.useState('');
    const {
        data: internalComments,
        isLoading: internalLoading,
        error: internalError,
        refetch: refetchInternal
    } = useGetDocCommentsQuery({ tenant: tenant!, docId }, { skip: isExternal });

    const {
        data: externalComments,
        isLoading: externalLoading,
        error: externalError,
        refetch: refetchExternal
    } = useGetExternalDocCommentsQuery({ docId }, { skip: !isExternal });

    const [createInternalComment, { isLoading: isCreatingInternal }] = useCreateDocCommentMutation();
    const [updateInternalComment, { isLoading: isUpdatingInternal }] = useUpdateDocCommentMutation();
    const [deleteInternalComment] = useDeleteDocCommentMutation();

    const [createExternalComment, { isLoading: isCreatingExternal }] = useCreateExternalDocCommentMutation();
    const [updateExternalComment, { isLoading: isUpdatingExternal }] = useUpdateExternalDocCommentMutation();
    const [deleteExternalComment] = useDeleteExternalDocCommentMutation();

    const comments = isExternal ? externalComments : internalComments;
    const commentsLoading = isExternal ? externalLoading : internalLoading;
    const commentsError = isExternal ? externalError : internalError;
    const refetch = isExternal ? refetchExternal : refetchInternal;
    const isCreating = isExternal ? isCreatingExternal : isCreatingInternal;
    const isUpdating = isExternal ? isUpdatingExternal : isUpdatingInternal;

    const handleCreateComment = async () => {
        if (!comment.trim()) return;
        try {
            if (isExternal) {
                await createExternalComment({
                    data: {
                        resource: docId,
                        comments: comment
                    }
                }).unwrap();
            } else {
                await createInternalComment({
                    tenant: tenant!,
                    data: {
                        resource: docId,
                        comments: comment
                    }
                }).unwrap();
            }
            setComment('');
            toast.success('Comment added');
        } catch (error) {
            toast.error('Failed to add comment');
        }
    };

    const handleUpdateComment = async (commentId: number) => {
        if (!editValue.trim()) return;
        try {
            if (isExternal) {
                await updateExternalComment({
                    commentId,
                    docId,
                    data: {
                        id: commentId,
                        comments: editValue
                    }
                }).unwrap();
            } else {
                await updateInternalComment({
                    tenant: tenant!,
                    commentId,
                    docId,
                    data: {
                        id: commentId,
                        comments: editValue
                    }
                }).unwrap();
            }
            setEditingCommentId(null);
            toast.success('Comment updated');
        } catch (error) {
            toast.error('Failed to update comment');
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        try {
            if (isExternal) {
                await deleteExternalComment({
                    commentId,
                    docId
                }).unwrap();
            } else {
                await deleteInternalComment({
                    tenant: tenant!,
                    commentId,
                    docId
                }).unwrap();
            }
            toast.success('Comment deleted');
        } catch (error) {
            toast.error('Failed to delete comment');
        }
    };

    return (
        <div className="space-y-10 py-10 border-t border-border/10">
            <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                    <MessageSquare className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-bold tracking-tight italic">Team Discussion</h3>
                <Badge className="ml-2 bg-primary/5 text-primary border-primary/10 rounded-full px-3">
                    {comments?.length || 0}
                </Badge>
            </div>

            {/* Add Comment */}
            <div className="space-y-4 bg-muted/20 p-6 rounded-2xl border border-border/5 shadow-inner group">
                <div className="min-h-[150px] rounded-xl border border-border/40 bg-background/50 overflow-hidden focus-within:ring-4 focus-within:ring-primary/5 transition-all shadow-sm">
                    <RichTextEditor
                        value={comment}
                        onChange={setComment}
                        placeholder="Join the conversation..."
                        className="min-h-[130px]"
                    />
                </div>
                <div className="flex justify-end">
                    <Button
                        onClick={handleCreateComment}
                        disabled={isCreating || !comment.trim()}
                    >
                        {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send Message
                    </Button>
                </div>
            </div>

            {/* Comments List */}
            <WithLoadingErrorWrapper
                loading={commentsLoading}
                error={commentsError as any}
                onRetry={refetch}
            >
                <div className="space-y-8 mt-10">
                    {comments && comments.length > 0 ? (
                        comments.map((c) => (
                            <div key={c.id} className="group flex gap-5 animate-in slide-in-from-bottom-2 duration-500">
                                <Avatar className="h-12 w-12 border-2 border-primary/10 shadow-md">
                                    <AvatarImage src={c.created_by.photo_url || ''} />
                                    <AvatarFallback className="bg-primary/5 text-primary font-bold">
                                        {c.created_by.first_name[0]}{c.created_by.last_name?.[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="font-bold text-base bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                                {c.created_by.first_name} {c.created_by.last_name}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full font-medium italic">
                                                <Calendar className="h-3 w-3 opacity-60" />
                                                {safeFormatDate(c.created_on, 'MMM d, h:mm a')}
                                            </span>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="rounded-xl border-border/40 backdrop-blur-xl">
                                                <DropdownMenuItem onClick={() => { setEditingCommentId(c.id); setEditValue(c.comments); }} className="gap-2 rounded-lg cursor-pointer">
                                                    <Pencil className="h-3.5 w-3.5" /> Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleDeleteComment(c.id)} className="gap-2 text-destructive focus:text-destructive rounded-lg cursor-pointer">
                                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>

                                    {editingCommentId === c.id ? (
                                        <div className="space-y-4 bg-muted/30 p-4 rounded-xl border border-border/5">
                                            <div className="min-h-[120px] rounded-lg border border-border/40 bg-background overflow-hidden">
                                                <RichTextEditor
                                                    value={editValue}
                                                    onChange={setEditValue}
                                                    className="min-h-[100px]"
                                                />
                                            </div>
                                            <div className="flex justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)} className="rounded-lg h-9 px-4">
                                                    Cancel
                                                </Button>
                                                <Button size="sm" onClick={() => handleUpdateComment(c.id)} disabled={isUpdating || !editValue.trim()} className="rounded-lg h-9 px-6 gap-2">
                                                    {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                                    Update
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative p-5 rounded-2xl rounded-tl-none bg-card/60 backdrop-blur-sm border border-border/40 shadow-sm group-hover:shadow-md transition-all duration-300">
                                            <ReadOnlyContentDisplay
                                                html={c.comments}
                                                className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed"
                                            />
                                            <div className="absolute -left-2 top-0 h-4 w-4 bg-card/60 border-l border-t border-border/40 -rotate-45" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-12 flex flex-col items-center justify-center bg-muted/10 rounded-2xl border-2 border-dashed border-border/40">
                            <div className="p-4 rounded-full bg-background mb-4 shadow-sm border border-border/60">
                                <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
                            </div>
                            <p className="text-muted-foreground font-medium italic">Quiet in here... Start the first discussion!</p>
                        </div>
                    )}
                </div>
            </WithLoadingErrorWrapper>
        </div>
    );
};

// Helper Badge component if not available, or just use the UI badge
function Badge({ children, className }: { children: React.ReactNode, className?: string }) {
    return (
        <span className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}>
            {children}
        </span>
    );
}

